/* ==============================================
   UNIÓN SUIZA — Wishlist JS
   Archivo: assets/wishlist.js
   ============================================== */

const Wishlist = {
  STORAGE_KEY: 'us_wishlist',

  get() { return JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '[]'); },
  save(items) { localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items)); },
  add(id) {
    const items = this.get();
    if (!items.includes(String(id))) { items.push(String(id)); this.save(items); }
  },
  remove(id) { this.save(this.get().filter(i => i !== String(id))); },
  has(id) { return this.get().includes(String(id)); },
  toggle(id) { this.has(id) ? this.remove(id) : this.add(id); return this.has(id); }
};

/* ════════════════════════════════════════════
   DRAWER
   ════════════════════════════════════════════ */
const Drawer = {
  drawer: null,
  overlay: null,
  list: null,
  loading: null,
  empty: null,

  init() {
    this.drawer  = document.getElementById('wl-drawer');
    this.overlay = document.getElementById('wl-overlay');
    this.list    = document.getElementById('wl-drawer-list');
    this.loading = document.getElementById('wl-drawer-loading');
    this.empty   = document.getElementById('wl-drawer-empty');
    if (!this.drawer) return;

    document.getElementById('wl-header-btn')
      ?.addEventListener('click', () => this.open());
    document.getElementById('wl-drawer-close')
      ?.addEventListener('click', () => this.close());
    document.getElementById('wl-drawer-browse')
      ?.addEventListener('click', () => { this.close(); window.location.href = '/collections/all'; });
    this.overlay.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
  },

  open() {
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.getElementById('wl-header-btn')?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('wl-open');
    this.render();
  },

  close() {
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.getElementById('wl-header-btn')?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('wl-open');
  },

  async render() {
    this.list.innerHTML = '';
    this.loading.style.display = 'block';
    this.empty.style.display = 'none';

    const ids = Wishlist.get();

    if (ids.length === 0) {
      this.loading.style.display = 'none';
      this.empty.style.display = 'flex';
      return;
    }

    try {
      const query = ids.map(id => `id:${id}`).join(' OR ');
      const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=24`);
      const data = await res.json();
      const products = data.resources.results.products;

      this.loading.style.display = 'none';

      if (!products.length) {
        this.empty.style.display = 'flex';
        return;
      }

      // Fetch variant IDs for each product via /products/{handle}.js
      const productsWithVariants = await Promise.all(
        products.map(async (p) => {
          try {
            const r = await fetch(`/products/${p.handle}.js`);
            const full = await r.json();
            return { ...p, variantId: full.variants?.[0]?.id, available: full.available };
          } catch {
            return { ...p, variantId: null, available: false };
          }
        })
      );

      productsWithVariants.forEach(p => this.renderItem(p));

    } catch(e) {
      this.loading.innerHTML = '<p style="color:#888;text-align:center;padding:24px">Error al cargar. Recarga la página.</p>';
    }
  },

  renderItem(p) {
    const rawPrice = parseFloat(p.price);
    const price = Number.isNaN(rawPrice)
      ? p.price
      : rawPrice.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const available = p.available !== false;

    const li = document.createElement('li');
    li.className = 'wl-drawer__item';
    li.dataset.productId = p.id;
    li.innerHTML = `
      <a class="wl-drawer__item-image" href="${p.url}">
        <img src="${p.featured_image?.url || p.image || ''}" alt="${p.title}" loading="lazy">
      </a>
      <div class="wl-drawer__item-info">
        <span class="wl-drawer__item-vendor">${p.vendor || ''}</span>
        <a class="wl-drawer__item-title" href="${p.url}">${p.title}</a>
        <span class="wl-drawer__item-price">${price} €</span>
        <div class="wl-drawer__item-actions">
          <button
            class="wl-drawer__add-cart"
            data-variant-id="${p.variantId || ''}"
            ${!available || !p.variantId ? 'disabled' : ''}
          >
            ${available ? 'Añadir a la cesta' : 'Sin stock'}
          </button>
          <button class="wl-drawer__remove" aria-label="Eliminar de favoritos">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    // ── Añadir a cesta ──
    li.querySelector('.wl-drawer__add-cart').addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.disabled = true;
      btn.textContent = 'Añadiendo…';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(variantId), quantity: 1 })
        });

        if (!res.ok) throw new Error('Cart error');

        btn.textContent = '¡Añadido!';
        btn.classList.add('added');

        await refreshCart();

        setTimeout(() => {
          btn.textContent = 'Añadir a la cesta';
          btn.classList.remove('added');
          btn.disabled = false;
        }, 2500);

      } catch {
        btn.textContent = 'Reintentar';
        btn.disabled = false;
      }
    });

    // ── Eliminar de favoritos ──
    li.querySelector('.wl-drawer__remove').addEventListener('click', () => {
      trackWishlistEvent('remove_from_wishlist', {
        product_id:     p.id,
        product_handle: p.handle || '',
        product_name:   p.title  || '',
      });

      Wishlist.remove(p.id);
      li.remove();
      updateCounter();
      if (Wishlist.get().length === 0) this.empty.style.display = 'flex';

      document.querySelectorAll(
        `.wl-heart-btn[data-product-id="${p.id}"],
         .wl-pdp-btn[data-product-id="${p.id}"]`
      ).forEach(btn => {
        btn.classList.remove('is-added');
        const label = btn.querySelector('.wl-pdp-btn__label');
        if (label) label.textContent = 'Añadir a Favoritos';
      });
    });

    this.list.appendChild(li);
  }
};

/* ════════════════════════════════════════════
   BOTONES CORAZÓN — sin login obligatorio
   ════════════════════════════════════════════ */
function bindHeartButton(btn) {
  if (btn.dataset.wlBound) return;
  btn.dataset.wlBound = '1';
  const id = btn.dataset.productId;
  btn.classList.toggle('is-added', Wishlist.has(id));
  btn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    const added = Wishlist.toggle(id);
    btn.classList.toggle('is-added', added);
    updateCounter();

    trackWishlistEvent(added ? 'add_to_wishlist' : 'remove_from_wishlist', {
      product_id:     id,
      product_handle: btn.dataset.productHandle || '',
      product_name:   btn.dataset.productTitle  || '',
    });
  });
}

function initHeartButtons() {
  document.querySelectorAll('.wl-heart-btn').forEach(bindHeartButton);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        if (node.classList?.contains('wl-heart-btn')) bindHeartButton(node);
        node.querySelectorAll?.('.wl-heart-btn').forEach(bindHeartButton);
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

/* ════════════════════════════════════════════
   BOTÓN PDP — sin login obligatorio
   ════════════════════════════════════════════ */
function initPdpButton() {
  const btn = document.querySelector('.wl-pdp-btn');
  if (!btn) return;
  const id    = btn.dataset.productId;
  const title = btn.dataset.productTitle || '';
  const label = btn.querySelector('.wl-pdp-btn__label');
  setPdpState(btn, label, Wishlist.has(id));

  btn.addEventListener('click', () => {
    const isAdded = Wishlist.toggle(id);
    setPdpState(btn, label, isAdded);
    updateCounter();
    if (isAdded) Drawer.open();

    trackWishlistEvent(isAdded ? 'add_to_wishlist' : 'remove_from_wishlist', {
      product_id:     id,
      product_handle: btn.dataset.productHandle || '',
      product_name:   title,
    });
  });
}

function setPdpState(btn, label, isAdded) {
  btn.classList.toggle('is-added', isAdded);
  if (label) label.textContent = isAdded ? 'Añadido a Favoritos' : 'Añadir a Favoritos';
}

/* ════════════════════════════════════════════
   CONTADOR HEADER
   ════════════════════════════════════════════ */
function updateCounter() {
  const count = Wishlist.get().length;
  document.querySelectorAll('.wl-counter').forEach(el => {
    el.textContent = count;
    el.style.display = count > 0 ? 'flex' : 'none';
  });
}

/* ════════════════════════════════════════════
   ACTUALIZAR CARRITO DEL TEMA
   ════════════════════════════════════════════ */
async function refreshCart() {
  try {
    const cartRes = await fetch('/cart.js');
    const cart = await cartRes.json();
    const count = cart.item_count;

    document.querySelectorAll(
      '[data-cart-count], .cart-count, .header__cart-count, ' +
      '#cart-icon-bubble .cart-count-bubble span, ' +
      '.cart-count-bubble span:first-child'
    ).forEach(el => { el.textContent = count; });

    document.querySelectorAll(
      '#cart-icon-bubble .cart-count-bubble, .cart-count-bubble'
    ).forEach(el => {
      el.style.display = count > 0 ? '' : 'none';
    });

    const sections = document.querySelector('[data-section-type="header"]')
      ?.dataset?.sectionId;

    if (sections) {
      const sectionRes = await fetch(`/?sections=${sections}`);
      const sectionData = await sectionRes.json();
      if (sectionData[sections]) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sectionData[sections], 'text/html');
        const newBubble = doc.querySelector('#cart-icon-bubble');
        const oldBubble = document.querySelector('#cart-icon-bubble');
        if (newBubble && oldBubble) {
          oldBubble.innerHTML = newBubble.innerHTML;
        }
      }
    }

    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: cart }));
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

  } catch(e) {
    console.warn('Wishlist: no se pudo actualizar el carrito', e);
  }
}

/* ════════════════════════════════════════════
   ANALYTICS — Wishlist events
   Compatible con GA4 via dataLayer (GTM)
   ════════════════════════════════════════════ */
function trackWishlistEvent(eventName, product) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: eventName,
      ecommerce: {
        items: [{
          item_id:      String(product.product_id   || ''),
          item_name:    product.product_name         || '',
          item_variant: product.product_handle       || '',
        }]
      }
    });
  } catch(e) {
    console.warn('Wishlist analytics error:', e);
  }
}

/* ════════════════════════════════════════════
   INIT
   ════════════════════════════════════════════ */
document.addEventListener('DOMContentLoaded', () => {
  Drawer.init();
  initHeartButtons();
  initPdpButton();
  updateCounter();
});